import { ArrowDownwardIcon } from "@mgdx/ui/components/Icon";
import { IconButton } from "@mgdx/ui/components/IconButton";
import clsx from "clsx";
import isEqual from "lodash/isEqual";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import Scroll from "react-scroll";
import usePrevious from "react-use/lib/usePrevious";

import styles from "./index.module.css";
import { TalkContent, TalkContentProps } from "./TalkContent";

const containerId = "talk-box-container";
const bottomId = "talk-box-bottom";
const hiddenScrollButtonOffset = 24;

export type OnVisibleUnreadMessage = (messageId: string) => void;

export type TalkBoxProps = Omit<TalkContentProps, "containment" | "onDidMount"> & {
  roomId: string;
  bottomContent?: ReactNode;
};

export const TalkBox = ({
  roomId,
  room,
  messages,
  talkSenderType,
  onVisibleUnreadMessage,
  bottomContent,
}: TalkBoxProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const prevLastMessage = usePrevious(messages?.at(-1));
  const [hiddenScrollButton, setHiddenScrollButton] = useState(true);

  const spyScroll = useCallback(() => {
    if (!containerRef.current) return;

    const { offsetHeight, scrollTop, scrollHeight } = containerRef.current;

    const value =
      scrollTop === 0 && offsetHeight === scrollHeight
        ? true
        : offsetHeight + scrollTop > scrollHeight - hiddenScrollButtonOffset;

    if (hiddenScrollButton === value) return;

    setHiddenScrollButton(value);
  }, [hiddenScrollButton]);

  const animateScrollToBottom = useCallback(() => {
    Scroll.animateScroll.scrollToBottom({
      containerId: containerId,
      duration: 250,
    });
  }, []);

  const scrollToBottom = useCallback(() => {
    Scroll.scroller.scrollTo(bottomId, {
      containerId: containerId,
    });
  }, []);

  useEffect(() => {
    if (isEqual(prevLastMessage, messages?.at(-1))) return;

    scrollToBottom();
  }, [messages, prevLastMessage, scrollToBottom]);

  useEffect(() => {
    const container = containerRef?.current;

    container?.addEventListener("scroll", spyScroll);

    return () => {
      container?.removeEventListener("scroll", spyScroll);
    };
  }, [spyScroll]);

  return (
    <div key={roomId} className={styles.talkBox}>
      <div ref={containerRef} className={styles.talkInner} id={containerId}>
        <TalkContent
          talkSenderType={talkSenderType}
          onVisibleUnreadMessage={onVisibleUnreadMessage}
          room={room}
          messages={messages}
          containment={containerRef.current}
          onDidMount={() => scrollToBottom()}
        />

        {bottomContent}

        <div id={bottomId} className={styles.bottom} />
      </div>

      <div className={clsx(styles.scrollButton, hiddenScrollButton && styles.hiddenScrollButton)}>
        <IconButton
          variant="lighted"
          color="key"
          size="s"
          onClick={animateScrollToBottom}
          aria-label="最新の位置に移動する"
        >
          <ArrowDownwardIcon />
        </IconButton>
      </div>
    </div>
  );
};
